import Big from "big.js";

export const getCurrencySymbol = (currency: string, locale = "en-US"): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  })
    .formatToParts()
    .find((part) => part.type === "currency")?.value ?? "";

export const getNumberOfDecimals = (
  number: number,
  currency: string,
  locale: string
): number => {
  const fraction =
    new Intl.NumberFormat(locale, { style: "currency", currency })
      .formatToParts(number)
      .find((part) => part.type === "fraction")?.value ?? "";

  return fraction.length;
};

/**
 * Parse a localized number to a float.
 * https://stackoverflow.com/questions/29255843/is-there-a-way-to-reverse-the-formatting-by-intl-numberformat-in-javascript
 */
export const parseMonetaryNumber = (
  stringNumber: string,
  currency: string,
  locale: string
): number => {
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, "");
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, "");

  const currencySymbol = getCurrencySymbol(currency, locale);

  return parseFloat(
    stringNumber
      .replace(currencySymbol, "")
      .replace(new RegExp("\\" + thousandSeparator, "g"), "")
      .replace(new RegExp("\\" + decimalSeparator), ".")
  );
};

export const formatMonetaryNumber = (
  number: number,
  currency: string,
  locale: string
): string =>
  new Intl.NumberFormat(locale, { style: "currency", currency })
    .format(number)
    // Replacing &nbsp with space
    .replace(new RegExp(String.fromCharCode(160), "g"), " ");

// Convert the amount into a pretty version and then revert it in case there are different decimal or thousands symbols
export const processAmount = (
  price = 0,
  currency = "USD",
  locale = "en-US'"
): string => {
  const formattedAmount = formatMonetaryNumber(price, currency, locale);
  const parsed = parseMonetaryNumber(formattedAmount, currency, locale);
  if (isNaN(parsed)) {
    return "0";
  }
  const parsedBig = new Big(parsed).times(new Big(100));
  return parsedBig.toFixed(0);
};
